import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
    APPEARANCE_DARK,
    APPEARANCE_LIGHT,
    APPEARANCE_RANDOM,
} from "../lib/appearance";
import { BG_IMAGE_IDS, BG_IMAGE_LABELS, BG_IMAGE_CATALOG } from "../lib/bgAssets";
import { APP_SITE_ID } from "../../../config/appSiteLinks";
import MobileAppLegalLinks from "../../mobile-common/MobileAppLegalLinks";
import WordWheelCreditsPointsSection from "../components/WordWheelCreditsPointsSection";
import { useWordWheelAppearance } from "../context/WordWheelAppearanceContext";
import { MAIN_PATH, PLAY_PATH } from "../routes";

const APPEARANCE_OPTIONS = [
    {
        id: APPEARANCE_LIGHT,
        label: "Light",
        description: "Ocean cyan play chrome",
        preview: "linear-gradient(180deg, #38bdf8 0%, #0284c7 40%, #0c4a6e 100%)",
    },
    {
        id: APPEARANCE_DARK,
        label: "Dark",
        description: "Deeper ocean play chrome",
        preview: "linear-gradient(180deg, #22d3ee 0%, #0284c7 40%, #020617 100%)",
    },
    {
        id: APPEARANCE_RANDOM,
        label: "Image",
        description: "Rotating scene photos (3 days each)",
        preview: null,
    },
];

export default function SettingsPage() {
    const location = useLocation();
    const { appearance, setAppearance, homeColors, sceneBackground, isRandomScene } =
        useWordWheelAppearance();
    const colors = homeColors;
    const backPath = location.state?.backPath || PLAY_PATH;

    const imagePreviewUrl =
        sceneBackground?.url || BG_IMAGE_CATALOG[BG_IMAGE_IDS[0]] || "";

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                color: colors.text,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    minHeight: 44,
                }}
            >
                <IconButton
                    component={RouterLink}
                    to={backPath}
                    aria-label="Back"
                    sx={{ minWidth: 44, minHeight: 44, color: colors.text }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h6"
                    sx={{
                        flex: 1,
                        fontWeight: 800,
                        color: colors.primary,
                        textAlign: "center",
                        pr: 5.5,
                    }}
                >
                    Settings
                </Typography>
            </Box>

            <Typography
                variant="overline"
                sx={{
                    display: "block",
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    color: colors.textMuted,
                    mb: 1,
                }}
            >
                Appearance
            </Typography>

            <Box sx={{ display: "flex", gap: 1.25, mb: 1.5, flexWrap: "wrap" }}>
                {APPEARANCE_OPTIONS.map((option) => {
                    const selected = appearance === option.id;
                    const preview =
                        option.id === APPEARANCE_RANDOM
                            ? undefined
                            : option.preview;
                    return (
                        <Box
                            key={option.id}
                            component="button"
                            type="button"
                            onClick={() => setAppearance(option.id)}
                            sx={{
                                flex: "1 1 30%",
                                minWidth: 96,
                                m: 0,
                                p: 1.25,
                                border: selected
                                    ? `2px solid ${colors.primary}`
                                    : `1px solid ${colors.border}`,
                                borderRadius: 2,
                                bgcolor: colors.surface,
                                cursor: "pointer",
                                textAlign: "left",
                                boxShadow: selected
                                    ? `0 0 0 2px ${colors.successSoft || "rgba(5, 150, 105, 0.15)"}`
                                    : colors.shadowSm,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    height: 56,
                                    borderRadius: 1.5,
                                    mb: 1,
                                    background: preview || undefined,
                                    backgroundImage:
                                        option.id === APPEARANCE_RANDOM
                                            ? `url("${imagePreviewUrl}")`
                                            : undefined,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                {selected && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 6,
                                            right: 6,
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            bgcolor: colors.primary,
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CheckIcon sx={{ fontSize: 14 }} />
                                    </Box>
                                )}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: colors.text }}>
                                {option.label}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: colors.textMuted, lineHeight: 1.35, display: "block" }}
                            >
                                {option.description}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {isRandomScene ? (
                <>
                    <Typography
                        variant="caption"
                        sx={{ display: "block", color: colors.textMuted, mb: 1.5, lineHeight: 1.4 }}
                    >
                        Scene photos rotate every 3 days. Current:{" "}
                        <strong>
                            {BG_IMAGE_LABELS[sceneBackground?.id] || sceneBackground?.id || "Deep sea"}
                        </strong>
                        .
                    </Typography>
                    <Typography
                        variant="overline"
                        sx={{
                            display: "block",
                            letterSpacing: 1.2,
                            fontWeight: 700,
                            color: colors.textMuted,
                            mb: 1,
                        }}
                    >
                        Theme gallery
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(5, 1fr)",
                            gap: 0.75,
                            mb: 2,
                        }}
                    >
                        {BG_IMAGE_IDS.map((id) => {
                            const active = sceneBackground?.id === id;
                            return (
                                <Box
                                    key={id}
                                    title={BG_IMAGE_LABELS[id] || id}
                                    sx={{
                                        aspectRatio: "1",
                                        borderRadius: 1.25,
                                        backgroundImage: `url("${BG_IMAGE_CATALOG[id]}")`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        border: active
                                            ? `2px solid ${colors.primary}`
                                            : `1px solid ${colors.border}`,
                                        boxShadow: active
                                            ? `0 0 0 2px ${colors.successSoft}`
                                            : "none",
                                    }}
                                />
                            );
                        })}
                    </Box>
                </>
            ) : null}

            <Typography
                variant="overline"
                sx={{
                    display: "block",
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    color: colors.textMuted,
                    mb: 1,
                    mt: 0.5,
                }}
            >
                Credits &amp; coins
            </Typography>

            <WordWheelCreditsPointsSection />

            <Box sx={{ mt: 1 }}>
                <MobileAppLegalLinks appId={APP_SITE_ID.WORD_WHEEL_QUEST} />
            </Box>

            <Typography
                variant="caption"
                sx={{ display: "block", mt: 2, mb: 1, color: colors.textMuted, textAlign: "center" }}
            >
                <RouterLink to={MAIN_PATH} style={{ color: "inherit" }}>
                    Back to home
                </RouterLink>
            </Typography>
        </Box>
    );
}
